/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Formik, FormikProps, Field as FormikField } from 'formik'
import { Form } from 'react-bootstrap'
import { useServiceContext } from '../../root'
import Modal from '../../components/Modal'
import Field from '../../components/shared/Field'
import { ErrorMessageStyled } from '../../components/ErrorMessage'
import Validation from '../../lib/Validation'
import { useEsignature } from '../../hooks/useEsignature'
import { generateFSTpls, type GenerateFSTRequest } from '../../services/api/etmf'
import { getFileIdByUuid } from '../../services/api/files'
import { File } from '../../constants/file'
import { size } from 'lodash'
import RcmEsignatureModal from '../../components/esignature/RcmEsignatureModal'

interface GenerateFSTModalProps {
  fsiId: number
  onClose?: () => void
}

interface FormValues {
  name: string
  core: boolean
  recommended: boolean
  product: boolean
  study: boolean
  environment: boolean
  country: boolean
  site: boolean
  device: boolean
  folderOnly: boolean
}

const GenerateFSTModal: React.FC<GenerateFSTModalProps> = ({ fsiId, onClose }) => {
  const context = useServiceContext()
  const { toast, auth, filter } = context.service
  const { translate } = filter
  const { signAction, showDsoModal, esignMessage, handleDsoSubmit, handleDsoClose } = useEsignature()

  const defaultFilterConst: FormValues = {
    name: '',
    core: true,
    recommended: true,
    product: true,
    study: true,
    environment: true,
    country: true,
    site: true,
    device: true,
    folderOnly: false
  }

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [targetFolderFsiId, setTargetFolderFsiId] = useState<number | null>(null)
  const [device, setDevice] = useState<boolean>(true)
  const [folderOnly, setFolderOnly] = useState<boolean>(false)

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [fsiId])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = async () => {
    try {
      const data = await getFileIdByUuid(File.uuid.FOLDER_STRUCTURE)
      setTargetFolderFsiId(data.id)
    } catch (error) {
      console.error('Failed to load folder structure UUID:', error)
    }
  }

  const save = async (values: FormValues) => {
    doClose()

    if (!targetFolderFsiId) {
      toast.error(translate('ERROR_OCCURRED'))
      return
    }

    const requestObj: Omit<GenerateFSTRequest, 'esignature'> = {
      name: values.name,
      core: values.core,
      device: values.device,
      excelTemplateFsiId: fsiId,
      targetFolderFsiId: targetFolderFsiId,
      product: values.product,
      study: values.study,
      environment: values.environment,
      country: values.country,
      recommended: values.recommended,
      site: values.site,
      folderOnly: values.folderOnly
    }

    // Check if e-signature is required
    const requireESignature = auth.requireESignatureForFileCommands()
    let eSignatureValidation = null

    if (requireESignature) {
      try {
        // Build e-signature message
        const esignMsg = translate('ADMIN_ACTION_ETMF_GENERATE_FOLDER_STRC_TPLS')

        // Show e-signature modal and wait for validation
        const validation = await signAction('ADMIN_ACTION', esignMsg)

        // Extract only the fields needed by the backend API
        // For SSO/OKTA: backend expects only {relayState}, not the full validation with result
        // For DSO: backend expects only {username, password}
        if (validation.relayState) {
          // SSO/OKTA authentication - extract only relayState
          eSignatureValidation = { relayState: validation.relayState }
        } else {
          // DSO authentication - extract only username/password
          eSignatureValidation = {
            username: validation.username || '',
            password: validation.password || ''
          }
        }
      } catch (error) {
        // User cancelled e-signature or authentication failed
        console.log('E-signature cancelled by user')
        return
      }
    }

    try {
      await generateFSTpls(requestObj, eSignatureValidation || undefined)
      toast.success(translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_MSG_SUCCESS'))
    } catch (error: any) {
      toast.error(translate('{{error}}', { error: error?.data?.message || error?.message }))
    }
  }

  return (
    <>
      <Formik
        validate={(values) => {
          const errors: Partial<Record<keyof FormValues, string>> = {}
          if (values.name.trim() === '') {
            errors.name = Validation.general.mixed.required
          }
          return errors
        }}
        enableReinitialize={true}
        initialValues={{ ...defaultFilterConst, device, folderOnly }}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, handleSubmit, setFieldValue }: FormikProps<FormValues>) => (
          <div>
            <Modal
              show={isOpen}
              handleClose={doClose}
              title={translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_TITLE')}
              className="generateFST"
            >
              <Modal.Body>
                <div className="form-group">
                  <div className="form-group" id="199579de-c080-4ca8-9fed-6c650e6a6b0b">
                    <Form.Label className="fs12 mb0">{translate('NAME')}</Form.Label>
                    <Field
                      autoFocus
                      type="text"
                      className="mb0 w50p pos-relative"
                      name="name"
                      maxLength={255}
                      id="a89fa85e-f439-4728-8509-410795a68d05"
                    />
                    <ErrorMessageStyled name="name" classesName="fs14" />
                  </div>

                  <div className="form-group" id="666a890a-cfd7-4f59-9562-c8ffa5cc6bcd">
                    <Form.Label className="fs12 mb0">
                      {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_STUDY_TYPE')}
                    </Form.Label>
                    <div className="radio">
                      <label className="fs12">
                        <input
                          type="radio"
                          name="device"
                          id="a21323f2-93e9-4631-b27a-46fe3ea818b4"
                          checked={device}
                          onChange={() => {
                            setDevice(true)
                            setFieldValue('device', true)
                          }}
                        />{' '}
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_DEVICE')}
                      </label>
                    </div>
                    <div className="radio">
                      <label className="fs12">
                        <input
                          type="radio"
                          name="device"
                          id="64da075b-4378-49bd-9dea-5b5d7f7576ab"
                          checked={!device}
                          onChange={() => {
                            setDevice(false)
                            setFieldValue('device', false)
                          }}
                        />{' '}
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_NONDEVICE')}
                      </label>
                    </div>
                  </div>

                  <div className="form-group" id="4ba9e95b-5de0-4f12-b1da-9976fb38fe3f">
                    <Form.Label className="fs12 mb0">
                      {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_CORE_RECOMMENDED')}
                    </Form.Label>
                    <div className="checkbox">
                      <label htmlFor="39c19999-ce12-4a7e-8246-59bbe1f10c27" className="fs12">
                        <FormikField
                          name="core"
                          type="checkbox"
                          checked={values.core}
                          id="39c19999-ce12-4a7e-8246-59bbe1f10c27"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_CORE')}
                      </label>
                    </div>
                    <div className="checkbox">
                      <label htmlFor="74a8afa2-0fe7-4033-bb47-5221307245c5" className="fs12">
                        <FormikField
                          name="recommended"
                          type="checkbox"
                          checked={values.recommended}
                          id="74a8afa2-0fe7-4033-bb47-5221307245c5"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_RECOMMENDED')}
                      </label>
                    </div>
                  </div>

                  <div className="form-group" id="8fdd725e-c75e-4480-9aef-b67a7fd78f9f">
                    <Form.Label className="fs12 mb0">
                      {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_LEVELS')}
                    </Form.Label>
                    <div className="checkbox">
                      <label htmlFor="de43d5dd-1ea5-4b8f-97ba-74b018a4c0e6" className="fs12">
                        <FormikField
                          name="product"
                          type="checkbox"
                          checked={values.product}
                          id="de43d5dd-1ea5-4b8f-97ba-74b018a4c0e6"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_PRODUCT')}
                      </label>
                    </div>
                    <div className="checkbox">
                      <label htmlFor="3f20292f-c131-48a2-b55d-3e40c10f43ae" className="fs12">
                        <FormikField
                          name="study"
                          type="checkbox"
                          checked={values.study}
                          id="3f20292f-c131-48a2-b55d-3e40c10f43ae"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_STUDY')}
                      </label>
                    </div>
                    <div className="checkbox">
                      <label htmlFor="892fc393-0d0f-4add-8af9-56fcf5ea4dc2" className="fs12">
                        <FormikField
                          name="environment"
                          type="checkbox"
                          checked={values.environment}
                          id="892fc393-0d0f-4add-8af9-56fcf5ea4dc2"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_ENVIRONMENT')}
                      </label>
                    </div>
                    <div className="checkbox">
                      <label htmlFor="ac5b68bf-cf50-4060-93db-0c667731e99a" className="fs12">
                        <FormikField
                          name="country"
                          type="checkbox"
                          checked={values.country}
                          id="ac5b68bf-cf50-4060-93db-0c667731e99a"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_COUNTRY')}
                      </label>
                    </div>
                    <div className="checkbox">
                      <label htmlFor="483f9b37-1dd4-410f-a867-cdcd184b5296" className="fs12">
                        <FormikField
                          name="site"
                          type="checkbox"
                          checked={values.site}
                          id="483f9b37-1dd4-410f-a867-cdcd184b5296"
                        />
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_SITE')}
                      </label>
                    </div>
                  </div>

                  <div className="form-group" id="cbb0bef3-7408-4abc-abd8-8b7ef491c45b">
                    <Form.Label className="fs12 mb0">
                      {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_GEN_TYPE')}
                    </Form.Label>
                    <div className="radio">
                      <label className="fs12">
                        <input
                          type="radio"
                          name="foldersandplaceholders"
                          id="73006550-8856-4e37-bb5c-1c49620e7a1b"
                          checked={!folderOnly}
                          onChange={() => {
                            setFolderOnly(false)
                            setFieldValue('folderOnly', false)
                          }}
                        />{' '}
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_FOLDER')}
                      </label>
                    </div>
                    <div className="radio">
                      <label className="fs12">
                        <input
                          type="radio"
                          name="foldersandplaceholders"
                          id="d1c26eb5-f91a-4575-a70c-b439cc7f7831"
                          checked={folderOnly}
                          onChange={() => {
                            setFolderOnly(true)
                            setFieldValue('folderOnly', true)
                          }}
                        />{' '}
                        {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_LABEL_FOLDERS_ONLY')}
                      </label>
                    </div>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <button className="btn btn-link" onClick={doClose} id="9c293aa4-fc21-47cb-8dcf-ddcd25b52b61">
                  {translate('CANCEL')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => handleSubmit()}
                  id="143ecbe3-c6fb-4902-8d22-0aaf1eeb1baa"
                >
                  {translate('ETMF_GENERATE_FOLDER_STRCT_TPLS_BTN_GENERATE')}
                </button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </Formik>
      {/* E-Signature Modal (for DSO/RCM authentication) */}
      {showDsoModal && (
        <RcmEsignatureModal onSubmit={handleDsoSubmit} onClose={handleDsoClose} esignMessage={esignMessage} />
      )}
    </>
  )
}

export default GenerateFSTModal
